import { type JsonMessage, Listeners, type Room, type Transport } from '../transport';

/**
 * Trystero (WebRTC mesh) transport. Signaling runs over public Nostr relays
 * and WebTorrent trackers in parallel (both keyless); the dev/CI variant uses
 * a local ws-relay. Reliable messages use Trystero's ordered data channel;
 * snapshots use an extra negotiated unreliable channel on the same
 * RTCPeerConnection (research 04 §3.2).
 *
 * Trystero 0.25.4 is patched (patches/@trystero-p2p+core+0.25.4.patch): it
 * refreshed pre-built offers older than ~57 s with a rollback that, in
 * Chrome, drops the data channel from the offer, so a player who had been in
 * a room for a minute could not connect to half of the newcomers.
 */

export const PROTOCOL_VERSION = 1;
export const APP_ID = `aetherfall-online-net-v${PROTOCOL_VERSION}`;

/** Curated Nostr relays that passed the echo probe (research 04 §2.2). */
export const NOSTR_RELAYS = [
  'wss://nos.lol',
  'wss://relay.primal.net',
  'wss://relay.snort.social',
  'wss://nostr.mom',
  'wss://offchain.pub',
  'wss://nostr-01.yakihonne.com',
];

const UNRELIABLE_ID = 42;
/** Busy devices (loading a zone, compiling shaders) can need longer than Trystero's 10 s default. */
const HANDSHAKE_TIMEOUT_MS = 30_000;

interface TrysteroAction<T> {
  send: (data: T, opts?: { target?: string | string[] }) => Promise<void>;
  onMessage: ((data: T, ctx: { peerId: string }) => void) | null;
}

interface TrysteroRoom {
  makeAction: <T>(ns: string) => TrysteroAction<T>;
  getPeers: () => Record<string, RTCPeerConnection>;
  onPeerJoin: ((peerId: string) => void) | null;
  onPeerLeave: ((peerId: string) => void) | null;
  leave: () => Promise<void>;
}

type JoinFn = (config: Record<string, unknown>, roomId: string, callbacks?: Record<string, unknown>) => TrysteroRoom;

/** One unreliable channel per RTCPeerConnection (Trystero shares pcs across rooms). */
const unreliableChannels = new WeakMap<RTCPeerConnection, RTCDataChannel>();
const unreliableHandlers = new WeakMap<RTCPeerConnection, Set<(bytes: Uint8Array) => void>>();

function attachUnreliable(pc: RTCPeerConnection, onMsg: (b: Uint8Array) => void): RTCDataChannel | null {
  let set = unreliableHandlers.get(pc);
  if (!set) {
    set = new Set();
    unreliableHandlers.set(pc, set);
  }
  set.add(onMsg);
  let ch = unreliableChannels.get(pc);
  if (ch) return ch;
  try {
    ch = pc.createDataChannel('u', { negotiated: true, id: UNRELIABLE_ID, ordered: false, maxRetransmits: 0 });
  } catch {
    return null;
  }
  ch.binaryType = 'arraybuffer';
  const handlers = set;
  ch.onmessage = (e: MessageEvent<ArrayBuffer>) => {
    const bytes = new Uint8Array(e.data);
    for (const h of handlers) h(bytes);
  };
  unreliableChannels.set(pc, ch);
  return ch;
}

class MultiRoom implements Room {
  readonly msgs = new Listeners<[string, JsonMessage]>();
  readonly bins = new Listeners<[string, Uint8Array]>();
  readonly joins = new Listeners<[string]>();
  readonly leaves = new Listeners<[string]>();
  private known = new Set<string>();
  /** Which underlying room currently reaches each peer. */
  private route = new Map<string, TrysteroRoom>();
  private actions = new Map<TrysteroRoom, { r: TrysteroAction<JsonMessage>; b: TrysteroAction<ArrayBuffer> }>();
  private leftFlag = false;

  constructor(
    readonly id: string,
    readonly selfId: string,
    private rooms: TrysteroRoom[],
  ) {
    for (const room of rooms) this.wire(room);
  }

  private wire(room: TrysteroRoom): void {
    const r = room.makeAction<JsonMessage>('r');
    const b = room.makeAction<ArrayBuffer>('b');
    this.actions.set(room, { r, b });
    r.onMessage = (data, ctx) => {
      this.seen(ctx.peerId, room);
      this.msgs.emit(ctx.peerId, data);
    };
    b.onMessage = (data, ctx) => {
      this.seen(ctx.peerId, room);
      this.bins.emit(ctx.peerId, new Uint8Array(data));
    };
    room.onPeerJoin = (peerId) => this.seen(peerId, room);
    room.onPeerLeave = (peerId) => {
      if (this.route.get(peerId) !== room) return;
      // Another signaling room may still reach this peer.
      const alt = this.rooms.find((o) => o !== room && peerId in o.getPeers());
      if (alt) {
        this.route.set(peerId, alt);
        return;
      }
      this.route.delete(peerId);
      if (this.known.delete(peerId)) this.leaves.emit(peerId);
    };
  }

  private seen(peerId: string, room: TrysteroRoom): void {
    if (!this.route.has(peerId)) this.route.set(peerId, room);
    if (this.known.has(peerId)) return;
    this.known.add(peerId);
    const pc = room.getPeers()[peerId];
    if (pc) attachUnreliable(pc, (bytes) => this.bins.emit(peerId, bytes));
    this.joins.emit(peerId);
  }

  peers(): string[] {
    return [...this.known];
  }

  private targets(to?: string | readonly string[]): string[] {
    if (to === undefined) return this.peers();
    return typeof to === 'string' ? [to] : [...to];
  }

  send(msg: JsonMessage, to?: string | readonly string[]): void {
    if (this.leftFlag) return;
    for (const peer of this.targets(to)) {
      const room = this.route.get(peer);
      const a = room && this.actions.get(room);
      a?.r.send(msg, { target: peer }).catch(() => {});
    }
  }

  sendBinary(bytes: Uint8Array, to?: string | readonly string[]): void {
    if (this.leftFlag) return;
    for (const peer of this.targets(to)) {
      const room = this.route.get(peer);
      if (!room) continue;
      const pc = room.getPeers()[peer];
      const ch = pc ? unreliableChannels.get(pc) : undefined;
      if (ch && ch.readyState === 'open') {
        // Drop snapshots under congestion rather than queueing stale state.
        if (ch.bufferedAmount < 64 * 1024) ch.send(bytes as Uint8Array<ArrayBuffer>);
      } else {
        const a = this.actions.get(room);
        a?.b.send(bytes.slice().buffer, { target: peer }).catch(() => {});
      }
    }
  }

  onMessage(cb: (from: string, msg: JsonMessage) => void): () => void {
    return this.msgs.add(cb);
  }
  onBinary(cb: (from: string, bytes: Uint8Array) => void): () => void {
    return this.bins.add(cb);
  }
  onPeerJoin(cb: (id: string) => void): () => void {
    return this.joins.add(cb);
  }
  onPeerLeave(cb: (id: string) => void): () => void {
    return this.leaves.add(cb);
  }

  async leave(): Promise<void> {
    if (this.leftFlag) return;
    this.leftFlag = true;
    await Promise.all(this.rooms.map((r) => r.leave().catch(() => {})));
    this.known.clear();
  }
}

export interface TrysteroOptions {
  /** 'public' = Nostr + torrent; 'relay' = local ws-relay (dev/CI). */
  mode: 'public' | 'relay';
  relayUrl?: string;
  /** Optional user-provided TURN servers. */
  turn?: RTCIceServer[];
}

export class TrysteroTransport implements Transport {
  readonly kind: 'trystero' | 'ws-relay';
  selfId = '';
  private joiners: JoinFn[] = [];
  private ready: Promise<void>;

  constructor(private opts: TrysteroOptions) {
    this.kind = opts.mode === 'relay' ? 'ws-relay' : 'trystero';
    this.ready = this.load();
  }

  private async load(): Promise<void> {
    if (this.opts.mode === 'relay') {
      const m = await import('@trystero-p2p/ws-relay');
      this.joiners = [m.joinRoom as unknown as JoinFn];
      this.selfId = m.selfId;
    } else {
      const [nostr, torrent] = await Promise.all([import('trystero'), import('@trystero-p2p/torrent')]);
      this.joiners = [nostr.joinRoom as unknown as JoinFn, torrent.joinRoom as unknown as JoinFn];
      this.selfId = nostr.selfId;
    }
  }

  init(): Promise<void> {
    return this.ready;
  }

  async join(roomId: string, opts: { password?: string } = {}): Promise<Room> {
    await this.ready;
    const config: Record<string, unknown> = { appId: APP_ID };
    if (opts.password) config.password = opts.password;
    if (this.opts.turn?.length) config.turnConfig = this.opts.turn;
    const rooms = this.joiners.map((join, i) => {
      const cfg = { ...config };
      if (this.opts.mode === 'relay') cfg.relayConfig = { urls: [this.opts.relayUrl ?? 'ws://127.0.0.1:8787'] };
      else if (i === 0) cfg.relayConfig = { urls: NOSTR_RELAYS, warnOnRelayFailure: false };
      return join(cfg, roomId, { handshakeTimeoutMs: HANDSHAKE_TIMEOUT_MS, onJoinError: (d: { error?: unknown }) => console.warn('p2p join error', d.error) });
    });
    return new MultiRoom(roomId, this.selfId, rooms);
  }
}
